/**
 * Error Boundary per catturare errori React e mostrare fallback UI.
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center h-screen bg-danger-bg p-8">
          <div className="bg-danger/10 p-4 rounded-full mb-4">
            <AlertTriangle className="w-12 h-12 text-danger" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Qualcosa è andato storto
          </h2>
          <p className="text-muted-foreground mb-6 text-center max-w-md">
            Si è verificato un errore imprevisto. Prova a ricaricare la pagina.
          </p>
          {this.state.error && (
            <details className="mb-6 w-full max-w-md group">
              <summary className="cursor-pointer text-sm text-muted-foreground mb-2 list-none">
                Dettagli errore
              </summary>
              <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-40 font-mono">
                {this.state.error.message}
              </pre>
            </details>
          )}
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="w-4 h-4" />
            Ricarica pagina
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
